import React, { FC, useCallback, useEffect, useState } from 'react';
import { DataTable, DataTableError, DataTablePaginator, useLocalDataTableData } from '@utmost.co/utmost-design-system';
import { useDeepMemo } from '@beeline.com/bp-design-system';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { useDataTableFiltersEvent } from 'src/integrations/mixpanel/hooks/useDataTableFiltersEvent';
import { useDataTableColumnConfigurations } from 'src/utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { SaveCustomFieldDefinitionDialog } from '../CustomFieldDefinitions/SaveCustomField/SaveCustomFieldDialog';
import { CustomFieldDefinition } from '../CustomFieldDefinitions/utils/CustomFields.types';
import { ObjectsToolbar } from './ObjectsToolbar';
import { GetObjectsDocument } from './utils/__generated__/GetObjects.graphql';
import { convertObjectToDataTableObject } from './utils/convertObjectToDataTableObject';
import { useObjectColumns } from './Columns/useObjectColumns';
import { ObjectVariables } from './utils/Objects.types';
import { useHistory, useLocation } from 'react-router';
import { CustomObjectValuesSideBar } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/CustomObjectValues';
import { IsFeatureOn } from 'common/featureFlag/IsFeatureOn';

interface Props {}

export const Objects: FC<React.PropsWithChildren<Props>> = () => {
  const { data, loading, error, get: getObjects, refetch } = useTypedQuery(GetObjectsDocument);
  const [variables, setVariables] = useState<ObjectVariables>();
  const [objectToEdit, setObjectToEdit] = useState<WithOptionalUid<CustomFieldDefinition>>();
  const [isEdit, setIsEditing] = useState<boolean>(false);
  const [saveObjectModal, setSaveObjectModal] = useState<boolean>(false);
  const [isValuesPanelOpen, setValuesPanelOpen] = useState(false);
  const [customObjectDefinitionUidSelectedToEditValues, setCustomObjectDefinitionUidSelectedToEditValues] =
    useState<string>();
  const [customObjectDefinitionNameSelectedToEditValues, setCustomObjectDefinitionNameSelectedToEditValues] =
    useState<string>();

  const onCloseCustomObjectValuesPanelHandler = useCallback(() => {
    setValuesPanelOpen(false);
    setCustomObjectDefinitionUidSelectedToEditValues(undefined);
    setCustomObjectDefinitionNameSelectedToEditValues(undefined);
  }, []);

  const onEditCustomObjectValuesPanelHandler = useCallback(
    (customObjectDefinitionUid: string, customObjectDefinitionName: string) => {
      setValuesPanelOpen(true);
      setCustomObjectDefinitionUidSelectedToEditValues(customObjectDefinitionUid);
      setCustomObjectDefinitionNameSelectedToEditValues(customObjectDefinitionName);
    },
    []
  );

  const onCloseHandler = useCallback(() => {
    setObjectToEdit(undefined);
    setSaveObjectModal(false);
    setIsEditing(false);
  }, []);

  const onEditHandler = useCallback((customDefinition: WithOptionalUid<CustomFieldDefinition>) => {
    setObjectToEdit(customDefinition);
    setIsEditing(true);
    setSaveObjectModal(true);
  }, []);

  const onAddHandler = useCallback((customDefinition: WithOptionalUid<CustomFieldDefinition>) => {
    setObjectToEdit(customDefinition);
    setIsEditing(false);
    setSaveObjectModal(true);
  }, []);

  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations('SETTINGS_OBJECTS');
  useDataTableFiltersEvent<DeepPartial<ObjectVariables>>(variables, 'SETTINGS_OBJECTS');

  const objects = useDeepMemo(() => convertObjectToDataTableObject(data), [data]);

  const totalCount = objects.length ?? 0;

  const { localPagination, searchValue, setLocalSearch } = useLocalDataTableData({
    networkData: objects,
    setLocalPagination: true,
    setSearch: { localSearchKeys: ['name', 'uid'], searchInSubRows: true }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    getObjects();
    // exclude getObjects to avoid unnecessary re-renders
  }, []);

  const columns = useObjectColumns({
    onAdd: onAddHandler,
    onEdit: onEditHandler,
    onSubmitDone: refetch,
    onEditCustomObjectValues: onEditCustomObjectValuesPanelHandler
  });

  return (
    <>
      <DataTable
        pageCount={localPagination.pageCount}
        data={localPagination.paginatedData ?? []}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        onVariablesChanged={setVariables}
        totalCount={totalCount}
        isFetching={loading}
        expandable
        querySettings={{
          manualSortBy: true,
          useParams: true,
          previousURLKey: 'settings.objects.v1'
        }}
        columns={columns}
      >
        <DataTableError error={!objects?.length && error?.message} useHistory={useHistory} useLocation={useLocation} />
        <ObjectsToolbar
          onSubmitDone={refetch}
          pageDataCount={(localPagination.paginatedData ?? []).length}
          totalCount={localPagination.totalCount}
          value={searchValue ?? ''}
          onValueChange={setLocalSearch}
        />

        <DataTablePaginator
          fetchNextPage={(page) => localPagination.setPageIndex(page.pageIndex)}
          pageIndex={localPagination.pageIndex}
        />
      </DataTable>
      {saveObjectModal && (
        <SaveCustomFieldDefinitionDialog
          customField={objectToEdit}
          isObject
          mode={isEdit ? 'edit' : 'add'}
          refetch={refetch}
          onClose={onCloseHandler}
        />
      )}
      <IsFeatureOn featureId="custom-object-values">
        <CustomObjectValuesSideBar
          customObjectDefinitionUid={customObjectDefinitionUidSelectedToEditValues}
          customObjectDefinitionName={customObjectDefinitionNameSelectedToEditValues}
          isOpen={isValuesPanelOpen}
          onClose={onCloseCustomObjectValuesPanelHandler}
        />
      </IsFeatureOn>
    </>
  );
};
